'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ServiceItem {
  title: string;
  description: string;
}

interface AudienceTab {
  id: string;
  name: string;
  label: string;
  icon: 'GlobeAltIcon' | 'BuildingOffice2Icon';
  tagline: string;
  services: ServiceItem[];
}

const audienceTabs: AudienceTab[] = [
  {
    id: 'international',
    name: 'International Companies',
    label: 'For International Companies',
    icon: 'GlobeAltIcon',
    tagline: 'Enter Azerbaijan & Central Asian markets with confidence',
    services: [
      {
        title: 'Market Research & Entry Strategy',
        description: 'Identify opportunities and barriers in Azerbaijan and Central Asia.',
      },
      {
        title: 'Trade Show & Exhibition Support',
        description: 'Organize participation, stands, and B2B meetings at key regional fairs.',
      },
      {
        title: 'Sales & Marketing Assistance',
        description: 'Localized campaigns, lead generation, and distribution setup.',
      },
      {
        title: 'Regulatory & Logistics Support',
        description: 'Guidance on customs, taxes, and local regulatory requirements.',
      },
    ],
  },
  {
    id: 'azerbaijani',
    name: 'Azerbaijani Companies',
    label: 'For Azerbaijani Companies',
    icon: 'BuildingOffice2Icon',
    tagline: 'Expand your business into Europe, Central Asia, and beyond',
    services: [
      {
        title: 'European Market Entry',
        description: 'Partner identification, private label, and distribution setup across Europe.',
      },
      {
        title: 'Central Asian Expansion',
        description: 'Focused market entry into Kazakhstan, Uzbekistan, and Kyrgyzstan.',
      },
      {
        title: 'Exhibitions & Business Missions Abroad',
        description: 'Ensure your presence at key international trade fairs and business events.',
      },
      {
        title: 'Marketing & Digital Support',
        description: 'SMM, consultative sales, and lead generation for international audiences.',
      },
    ],
  },
];

export default function ServicesTabSection() {
  const [activeTab, setActiveTab] = useState<string>(audienceTabs[0].id);

  const activeAudience = audienceTabs.find((tab) => tab.id === activeTab) || audienceTabs[0];

  return (
    <section id="services" className="section-padding bg-gradient-to-b from-secondary to-background">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fadeIn">
          <div className="inline-block px-4 py-2 bg-accent/10 rounded-full mb-4">
            <span className="text-xs font-bold uppercase tracking-widest text-accent">
              Our Services
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground">
            Tailored Solutions for Every Business
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            Whether you're entering new markets or expanding abroad, we have the expertise to guide you every step of the way.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center gap-4 mb-12 overflow-x-auto pb-4">
          {audienceTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wider transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground shadow-lg'
                  : 'bg-muted text-muted-foreground hover:bg-primary/20'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="grid md:grid-cols-2 gap-12 items-start animate-fadeIn">
          {/* Left: Icon & Title */}
          <div className="space-y-6">
            <div className="w-20 h-20 bg-accent rounded-2xl flex items-center justify-center shadow-lg">
              <Icon name={activeAudience.icon} size={40} className="text-white" />
            </div>

            <h3 className="text-3xl md:text-4xl font-heading font-bold text-foreground">
              {activeAudience.label}
            </h3>

            <p className="text-lg text-muted-foreground leading-relaxed">
              {activeAudience.tagline}
            </p>

            <div className="pt-4 p-5 bg-primary/5 rounded-2xl border border-primary/10">
              <p className="text-sm text-foreground font-medium leading-relaxed">
                💡 Our team provides end-to-end support — from initial market research and strategy through to execution, partnerships, and ongoing growth.
              </p>
            </div>
          </div>

          {/* Right: Services List */}
          <div className="card-base p-8 space-y-4">
            <h4 className="text-xl font-bold text-foreground mb-6 uppercase tracking-wider">
              What We Offer
            </h4>
            <ul className="space-y-5">
              {activeAudience.services.map((service, index) => (
                <li
                  key={index}
                  className="flex items-start gap-4 animate-fadeIn"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="mt-1 w-6 h-6 bg-success/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="CheckIcon" size={16} className="text-success" />
                  </div>
                  <div>
                    <span className="text-foreground font-bold block">{service.title}</span>
                    <span className="text-muted-foreground text-sm">{service.description}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}